import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import complianceService from '../../../services/complianceService';

const ComplianceDataTable = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    documentType: 'license',
    complianceStatus: 'pending',
    expirationDate: ''
  });

  const documentTypeOptions = [
    { value: 'license', label: 'License' },
    { value: 'certification', label: 'Certification' },
    { value: 'inspection_report', label: 'Inspection Report' },
    { value: 'safety_document', label: 'Safety Document' },
    { value: 'training_record', label: 'Training Record' },
    { value: 'audit_report', label: 'Audit Report' },
    { value: 'permit', label: 'Permit' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'restricted', label: 'Restricted' },
    { value: 'prohibited', label: 'Prohibited' }
  ];

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await complianceService?.getAllDocuments();
      setDocuments(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load compliance documents');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (document) => {
    setEditingId(document?.id);
    setEditingData({
      title: document?.title,
      description: document?.description,
      documentType: document?.documentType,
      complianceStatus: document?.complianceStatus,
      expirationDate: document?.expirationDate ? new Date(document.expirationDate)?.toISOString()?.split('T')?.[0] : ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      const updatedDoc = await complianceService?.updateDocument(editingId, {
        ...editingData,
        expirationDate: editingData?.expirationDate ? new Date(editingData.expirationDate)?.toISOString() : null
      });
      
      setDocuments(prev => 
        prev?.map(doc => doc?.id === editingId ? { ...doc, ...editingData } : doc)
      );
      setEditingId(null);
      setEditingData({});
    } catch (err) {
      setError(err?.message || 'Failed to update document');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await complianceService?.deleteDocument(id);
      setDocuments(prev => prev?.filter(doc => doc?.id !== id));
    } catch (err) {
      setError(err?.message || 'Failed to delete document');
    }
  };

  const handleAddDocument = async (e) => {
    e?.preventDefault();
    try {
      const createdDoc = await complianceService?.createDocument({
        ...newDocument,
        expirationDate: newDocument?.expirationDate ? new Date(newDocument.expirationDate)?.toISOString() : null
      });
      
      setDocuments(prev => [createdDoc, ...prev]);
      setNewDocument({
        title: '',
        description: '',
        documentType: 'license',
        complianceStatus: 'pending',
        expirationDate: ''
      });
      setShowAddForm(false);
    } catch (err) {
      setError(err?.message || 'Failed to create document');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-success text-white';
      case 'pending': return 'bg-warning text-white';
      case 'restricted': return 'bg-accent text-white';
      case 'prohibited': return 'bg-error text-white';
      default: return 'bg-muted text-foreground';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const expirationDate = new Date(dateString);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow?.setDate(thirtyDaysFromNow?.getDate() + 30);
    return expirationDate <= thirtyDaysFromNow;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 agricultural-shadow">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={24} className="animate-spin text-primary mr-2" />
          <span className="text-muted-foreground">Loading compliance documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Compliance Documents</h3>
            <p className="text-sm text-muted-foreground">
              Manage regulatory documents and track compliance status
            </p>
          </div>
          <Button
            onClick={() => setShowAddForm(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Add Document
          </Button>
        </div>

        {error && (
          <div className="mt-4 bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-error mr-3" />
              <span className="text-error">{error}</span>
            </div>
          </div>
        )}
      </div>
      {/* Add Document Form */}
      {showAddForm && (
        <div className="p-6 border-b border-border bg-muted/20">
          <form onSubmit={handleAddDocument} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Document Title"
                value={newDocument?.title}
                onChange={(e) => setNewDocument({ ...newDocument, title: e?.target?.value })}
                placeholder="Enter document title"
                required
              />
              <Select
                label="Document Type"
                options={documentTypeOptions}
                value={newDocument?.documentType}
                onChange={(value) => setNewDocument({ ...newDocument, documentType: value })}
              />
              <Input
                label="Expiration Date"
                type="date"
                value={newDocument?.expirationDate}
                onChange={(e) => setNewDocument({ ...newDocument, expirationDate: e?.target?.value })}
              />
              <Select
                label="Status"
                options={statusOptions}
                value={newDocument?.complianceStatus}
                onChange={(value) => setNewDocument({ ...newDocument, complianceStatus: value })}
              />
            </div>
            <Input
              label="Description"
              value={newDocument?.description}
              onChange={(e) => setNewDocument({ ...newDocument, description: e?.target?.value })}
              placeholder="Enter document description"
            />
            <div className="flex space-x-2">
              <Button type="submit" iconName="Plus">Add Document</Button>
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Document
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Expiration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {documents?.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-muted-foreground">
                  No compliance documents found. Add your first document to get started.
                </td>
              </tr>
            ) : (
              documents?.map((document) => (
                <tr key={document?.id} className="hover:bg-muted/20">
                  {editingId === document?.id ? (
                    <>
                      <td className="px-6 py-4">
                        <Input
                          value={editingData?.title || ''}
                          onChange={(e) => setEditingData({ ...editingData, title: e?.target?.value })}
                          className="mb-2"
                        />
                        <Input
                          value={editingData?.description || ''}
                          onChange={(e) => setEditingData({ ...editingData, description: e?.target?.value })}
                          placeholder="Description"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <Select
                          options={documentTypeOptions}
                          value={editingData?.documentType || ''}
                          onChange={(value) => setEditingData({ ...editingData, documentType: value })}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <Select
                          options={statusOptions}
                          value={editingData?.complianceStatus || ''}
                          onChange={(value) => setEditingData({ ...editingData, complianceStatus: value })}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          type="date"
                          value={editingData?.expirationDate || ''}
                          onChange={(e) => setEditingData({ ...editingData, expirationDate: e?.target?.value })}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button size="sm" onClick={handleSaveEdit}>
                            <Icon name="Check" size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                            <Icon name="X" size={16} />
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-foreground">{document?.title}</div>
                          {document?.description && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {document?.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-foreground capitalize">
                          {document?.documentType?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(document?.complianceStatus)}`}>
                          {document?.complianceStatus?.charAt(0)?.toUpperCase() + document?.complianceStatus?.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-foreground">
                            {formatDate(document?.expirationDate)}
                          </span>
                          {isExpiringSoon(document?.expirationDate) && (
                            <Icon name="AlertTriangle" size={16} className="text-warning" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEdit(document)}
                          >
                            <Icon name="Edit" size={16} />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDelete(document?.id)}
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComplianceDataTable;